import { useMemo, useState } from 'react'

import {
  CellValueChangedEvent,
  DefaultMenuItem,
  GetRowIdParams,
  MenuItemDef,
} from 'ag-grid-enterprise'
import { isWeekend } from 'date-fns'
import { Mail, RotateCcw } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { createContextMenu } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DatePickerWithControls } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { TxReporting } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import {
  useResetTxReportRowMutation,
  useSendTxReportEmailMutation,
  useUpdateTxReportMutation,
} from '@/react-query/mutations/tx-reporting'
import { useTxReportQuery } from '@/react-query/queries/tx-reporting'

type ReportItem = TxReporting.GetTxReportData[number]

const getColDefs = (user: ReturnType<typeof useUserContext>) => {
  const hasWriteAccess = user.hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_TX_REPORTING],
  }))
  return createColDefs([
    { field: 'id', hide: true },
    { field: 'active', editable: hasWriteAccess, width: 100 },
    { field: 'category', rowGroup: true, hide: true },
    { field: 'modified_datetime_utc', headerName: 'Modified' },
    { field: 'Report Status', editable: hasWriteAccess },
    { field: 'Transaction Reference Number', editable: hasWriteAccess },

    { field: 'Firm Execution ID', editable: hasWriteAccess },
    { field: 'Instrument ID', editable: hasWriteAccess },

    { field: 'Venue Transaction ID', editable: hasWriteAccess },
    { field: 'Submitting Entity ID', editable: hasWriteAccess },
    { field: 'Executing Entity ID', editable: hasWriteAccess },
    { field: 'Investment Firm Indicator', editable: hasWriteAccess },
    { field: 'Buyer ID Type', editable: hasWriteAccess },
    { field: 'Buyer ID Sub Type', editable: hasWriteAccess },
    { field: 'Buyer ID', editable: hasWriteAccess },
    { field: 'Buyer Country of Branch', editable: hasWriteAccess },
    { field: 'Buyer First Name', editable: hasWriteAccess },
    { field: 'Buyer Surname', editable: hasWriteAccess },
    { field: 'Buyer DOB', editable: hasWriteAccess },
    { field: 'Buyer Decision Maker ID Type', editable: hasWriteAccess },
    { field: 'Buyer Decision Maker ID Sub Type', editable: hasWriteAccess },
    { field: 'Buyer Decision Maker ID', editable: hasWriteAccess },
    { field: 'Buyer Decision Maker First Name', editable: hasWriteAccess },
    { field: 'Buyer Decision Maker Surname', editable: hasWriteAccess },
    { field: 'Buyer Decision Maker DOB', editable: hasWriteAccess },
    { field: 'Seller ID Type', editable: hasWriteAccess },
    { field: 'Seller ID Sub Type', editable: hasWriteAccess },
    { field: 'Seller ID', editable: hasWriteAccess },
    { field: 'Seller Country of Branch', editable: hasWriteAccess },
    { field: 'Seller First Name', editable: hasWriteAccess },
    { field: 'Seller Surname', editable: hasWriteAccess },
    { field: 'Seller DOB', editable: hasWriteAccess },
    { field: 'Seller Decision Maker ID Type', editable: hasWriteAccess },
    { field: 'Seller Decision Maker ID Sub Type', editable: hasWriteAccess },
    { field: 'Seller Decision Maker ID', editable: hasWriteAccess },
    { field: 'Seller Decision Maker First Name', editable: hasWriteAccess },
    { field: 'Seller Decision Maker Surname', editable: hasWriteAccess },
    { field: 'Seller Decision Maker DOB', editable: hasWriteAccess },
    { field: 'Order Transmission Indicator', editable: hasWriteAccess },
    { field: 'Buyer Transmitter ID', editable: hasWriteAccess },
    { field: 'Seller Transmitter ID', editable: hasWriteAccess },
    { field: 'Trading Date Time', editable: hasWriteAccess },
    { field: 'Trading Capacity', editable: hasWriteAccess },
    { field: 'Quantity', editable: hasWriteAccess },
    { field: 'Quantity Type', editable: hasWriteAccess },
    { field: 'Quantity Currency', editable: hasWriteAccess },
    { field: 'Derivative Notional Change', editable: hasWriteAccess },
    { field: 'Price', editable: hasWriteAccess },
    { field: 'Price Type', editable: hasWriteAccess },
    { field: 'Price Currency', editable: hasWriteAccess },
    { field: 'Net Amount', editable: hasWriteAccess },
    { field: 'Venue', editable: hasWriteAccess },
    { field: 'Country of Branch', editable: hasWriteAccess },
    { field: 'Up-Front Payment', editable: hasWriteAccess },
    { field: 'Up-Front Payment Currency', editable: hasWriteAccess },
    { field: 'Complex Trade Component ID', editable: hasWriteAccess },
    { field: 'Instrument ID Type', editable: hasWriteAccess },
    { field: 'Instrument Name', editable: hasWriteAccess },
    { field: 'Instrument Classification', editable: hasWriteAccess },
    { field: 'Notional Currency 1', editable: hasWriteAccess },
    { field: 'Notional Currency 2', editable: hasWriteAccess },
    { field: 'Notional Currency 2 Type', editable: hasWriteAccess },
    { field: 'Price Multiplier', editable: hasWriteAccess },
    { field: 'UV Instrument Classification', editable: hasWriteAccess },
    { field: 'Underlying Instrument ID', editable: hasWriteAccess },
    { field: 'UV Index Classification', editable: hasWriteAccess },
    { field: 'Underlying Index ID', editable: hasWriteAccess },
    { field: 'Underlying Index Name', editable: hasWriteAccess },
    { field: 'Underlying Index Term', editable: hasWriteAccess },
    { field: 'Option Type', editable: hasWriteAccess },
    { field: 'Strike Price', editable: hasWriteAccess },
    { field: 'Strike Price Type, editable: hasWriteAccess' },
    { field: 'Strike Price Currency', editable: hasWriteAccess },
    { field: 'Option Style', editable: hasWriteAccess },
    { field: 'Maturity Date', editable: hasWriteAccess },
    { field: 'Expiry Date', editable: hasWriteAccess },
    { field: 'Delivery Type', editable: hasWriteAccess },
    { field: 'Investment Decision ID Type', editable: hasWriteAccess },
    { field: 'Investment Decision ID Sub Type', editable: hasWriteAccess },
    { field: 'Investment Decision ID', editable: hasWriteAccess },
    { field: 'Investment Decision Country of Branch', editable: hasWriteAccess },
    { field: 'Firm Execution ID Type', editable: hasWriteAccess },
    { field: 'Firm Execution ID Sub Type', editable: hasWriteAccess },

    { field: 'Firm Execution Country of Branch', editable: hasWriteAccess },
    { field: 'Waiver Indicator', editable: hasWriteAccess },
    { field: 'Short Selling Indicator', editable: hasWriteAccess },
    { field: 'OTC Post Trade Indicator', editable: hasWriteAccess },
    { field: 'Commodity Derivative Indicator', editable: hasWriteAccess },
    { field: 'SFT Indicator', editable: hasWriteAccess },
    { field: 'Internal Client Identification', editable: hasWriteAccess },
    { field: 'Data Category', editable: hasWriteAccess },
  ])
}

export interface TransactionReportingPageProps {}

export const TransactionReportingPage: React.FC<TransactionReportingPageProps> = ({}) => {
  const userContext = useUserContext()

  const [date, setDate] = useState(getTMinus1())
  const report_date = toISO8601(date)

  const reportQuery = useTxReportQuery({ report_date })

  const updateTxReportMutation = useUpdateTxReportMutation(report_date)
  const resetTxReportMutation = useResetTxReportRowMutation(report_date)
  const sendTxReportEmailMutation = useSendTxReportEmailMutation()

  const inputsDisabled =
    reportQuery.isFetching ||
    updateTxReportMutation.isPending ||
    sendTxReportEmailMutation.isPending ||
    resetTxReportMutation.isPending

  const hasWriteAccess = userContext.hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_TX_REPORTING],
  }))

  const reportData = useMemo(() => {
    return reportQuery.data ?? []
  }, [reportQuery.data])

  const colDefs = useMemo(() => {
    return getColDefs(userContext)
  }, [userContext])

  const getContextMenuItems = createContextMenu<ReportItem>(() => {
    const items: (DefaultMenuItem | MenuItemDef<ReportItem>)[] = [
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'separator',
      'export',
    ]

    if (hasWriteAccess) {
      items.unshift('separator')
      items.unshift({
        name: 'Reset row',
        action: (params) =>
          resetTxReportMutation.mutateAsync({
            category: params.node!.data?.category!,
            trade_id: params.node!.data?.id!,
          }),
        icon: renderToString(<RotateCcw size={16} />),
      } satisfies MenuItemDef<ReportItem>)
    }

    return items
  })

  return (
    <Page metadata={{ title: 'TX Reporting' }} permissions={OPERATIONS.TX_REPORTING.permissions}>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <DatePickerWithControls
            selected={date}
            disabled={(date) => isWeekend(date) || date > getTMinus1()}
            buttonDisabled={inputsDisabled}
            onSelect={(date) => {
              date && setDate(date)
            }}
            todayButton={{ enabled: true, date: getTMinus1(), text: 'T - 1' }}
          />
          <LoadingButton
            onClick={() => sendTxReportEmailMutation.mutateAsync({ report_date })}
            disabled={reportQuery.isFetching || inputsDisabled}
            variant={'outline'}
            icon={<Mail />}
            size="sm"
          >
            Send Email
          </LoadingButton>
        </div>
      </HeaderConfigBar>
      <Widget exposed className={'h-full'}>
        <WidgetHeaderWithQuery query={reportQuery} title="" />
        <WidgetContent loading={reportQuery.isLoading}>
          <CustomAgGrid
            domLayout={'normal'}
            rowData={reportData}
            getContextMenuItems={getContextMenuItems}
            columnDefs={colDefs}
            getRowId={(params: GetRowIdParams<ReportItem>) => params.data.id}
            updating={updateTxReportMutation.isPending}
            onCellValueChanged={(params: CellValueChangedEvent) => {
              return updateTxReportMutation.mutateAsync({
                newValue: params.newValue,
                oldValue: params.oldValue,
                rowData: params.data,
                updatedField: params.colDef.field!,
              })
            }}
          />
        </WidgetContent>
      </Widget>
    </Page>
  )
}
